import React, { useContext, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, Link } from 'react-router-dom';
import { Button, IconButton, Menu, MenuItem, Stack, Typography, Tooltip } from '@mui/material';
import { AccountCircle as ProfileIcon, ArrowBack, Menu as MenuIcon, HelpOutline } from '@mui/icons-material';
import { NavigationHeader } from '_lex/_atoms/NavigationHeader';

import { AppContext } from 'views/App/context';
import MainNav from '../../MainNav';
import useOrganization from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import AuthUtil from 'utils/AuthUtil';
import NotificationsNavIcon from '../../Notifications/NotificationsNavIcon';

export type TopNavProps = {};

const TopNav: React.FC<TopNavProps> = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const {
    currentProfile,
    isRoot,
    showNav,
    setShowNav,
    currentOrganizationId,
    isMobile,
    hasPermission,
    isLexipolStaff,
    isFeatureEnabled,
  } = useContext(AppContext);
  let defaultOrgName = localStorage.getItem('phx-currentOrganizationName')?.toString();
  if (defaultOrgName?.length === 0) defaultOrgName = currentProfile?.organization?.name || '';
  const [inOrgContext, setInOrgContext] = useState<boolean>(false);
  const organizationQuery = useOrganization(currentOrganizationId, {
    enabled: !!currentOrganizationId && hasPermission('getOrg', currentOrganizationId),
  });

  React.useEffect(() => {
    const inOrg = currentProfile?.user?.orgId && currentOrganizationId !== currentProfile?.user?.orgId;
    setInOrgContext(inOrg || false);
  }, [currentOrganizationId, currentProfile?.user?.orgId]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignout = async () => {
    AuthUtil.setSignOutCookie();
    AuthUtil.clearStorage();
    auth.signoutRedirect();
    setAnchorEl(null);
  };

  const handleMFASettings = () => {
    navigate('/admin/mfa-settings');
  };

  const handleKnowledgeBase = () => {
    navigate('/admin/kb');
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowNav(!showNav);
  };

  const leftItems = (
    <Stack
      direction='row'
      spacing={3}
      sx={{ alignItems: 'center' }}
    >
      {isMobile && (
        <>
          <IconButton
            onClick={handleMenuClick}
            aria-controls={showNav ? undefined : 'basic-menu'}
            aria-haspopup='true'
            aria-expanded={showNav ? 'false' : 'true'}
          >
            <MenuIcon sx={{ color: '#FFF' }} />
          </IconButton>
          <MainNav />
        </>
      )}
      {inOrgContext ? (
        <>
          {organizationQuery.data && organizationQuery.data.organization && (
            <Typography variant='h5'>{organizationQuery.data.organization.name}</Typography>
          )}
          <Button
            href='/admin/switch-org'
            size='small'
            startIcon={<ArrowBack />}
          >
            Return to Lexipol
          </Button>
        </>
      ) : (
        <>
          {currentProfile &&
            currentProfile.organization &&
            currentProfile.organization?.name?.toLowerCase() !== 'lexipol' ? (
            <Stack
              spacing={3}
              direction='row'
              sx={{ alignItems: 'center' }}
            >
              <img
                src='/admin/img/Lexipol-25-shield_wht-60.png'
                style={{ height: 24 }}
                alt='Lexipol'
              />
              <Typography variant='h5'>{currentProfile?.organization?.name}</Typography>
            </Stack>
          ) : (
            <img
              src='/admin/img/Lexipol-25-shield_wht-60.png'
              style={{ height: 24 }}
              alt='Lexipol'
            />
          )}
        </>
      )}
    </Stack>
  );

  const rightItems = (
    <Stack direction='row'>
      <Tooltip title={'Help'}>
        <IconButton
          href='https://lexipol-llc.my.site.com/HelpCenter/s/'
          target='_blank'
          rel='noopener noreferrer'
          color='inherit'
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>
      {!isRoot && <NotificationsNavIcon />}
      <div>
        <Tooltip title={[currentProfile?.user?.firstName, currentProfile?.user?.lastName].join(' ') || 'User'}>
          <IconButton
            size='large'
            aria-label='account of current user'
            aria-controls='menu-appbar'
            aria-haspopup='true'
            onClick={handleMenu}
            color='inherit'
          >
            <ProfileIcon />
          </IconButton>
        </Tooltip>
        <Menu
          id='menu-appbar'
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={handleSignout}>Log out</MenuItem>
          {isLexipolStaff && <MenuItem onClick={handleMFASettings}>MFA Settings</MenuItem>}
          {isLexipolStaff && isFeatureEnabled('knowledgeBase') && (
            <MenuItem onClick={handleKnowledgeBase}>Knowledge Base</MenuItem>
          )}
        </Menu>
      </div>
    </Stack>
  );

  return (
    <NavigationHeader
      global={true}
      leftItems={leftItems}
      rightItems={rightItems}
    />
  );
};

export default TopNav;
